import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO/SEO';
import {
  FaArrowRight,
  FaGlobeAmericas,
  FaCloud,
  FaRobot,
  FaCode,
  FaChartLine,
  FaAward,
  FaUsers,
  FaLightbulb,
  FaHandshake,
  FaRocket
} from 'react-icons/fa';
import softDeveImg from '../assets/developemt/softdeve.png';

// Assets could be replaced with actual images from the project or external sources
// For now, using placeholders or CSS gradients to match the aesthetic

const DevelopmentPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "Shaping Tomorrow's Digital Ecosystems",
      subtitle: "Shaping tomorrow's digital ecosystems, empowered by AI and human intelligence.",
      button: "Explore Our Services",
      image: "/assets/01.png",
      color: "bg-[#D81B60]"
    },
    {
      id: 2,
      title: "AI-Driven Innovation",
      subtitle: "Empowering businesses through AI-driven, human-focused innovation.",
      button: "Discover Solutions",
      image: "/assets/02.png",
      color: "bg-blue-600"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  // Parallax effects
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);
  const y2 = useTransform(scrollY, [0, 500], [0, -150]);

  const seoData = {
    title: "Development | Shaping Tomorrow With AI Today - Dhyon Technologies",
    description: "We empower businesses to be AI-first and human-centered through consulting, software engineering, and training excellence.",
    keywords: "AI, Software Engineering, Digital Transformation, Cloud Services, Automation"
  };

  return (
    <div className="bg-white text-gray-900 font-sans selection:bg-purple-100 selection:text-purple-900">
      <SEO {...seoData} />

      {/* Hero Section */}
      <section className="relative pt-40 pb-16 flex items-center overflow-hidden bg-white text-gray-900">
        <div className="container relative z-10 px-6 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-6xl"
          >
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-8">
              Pioneering AI Horizons & <br />
              <span className="text-4xl md:text-5xl bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500">Software Development</span>
            </h1>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-gray-400 flex flex-col items-center gap-2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-gray-400 to-transparent"></div>
        </motion.div>
      </section>

      {/* Image Slider Section */}
      <section className="relative h-[600px] w-full bg-gray-900 overflow-hidden group">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0"
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat transform hover:scale-105 transition-transform duration-[10s]"
              style={{ backgroundImage: `url(${slides[currentSlide].image})` }}
            />
            {/* Gradient Overlay for Text Readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>

            {/* Content */}
            <div className="absolute inset-0 flex items-center">
              <div className="container mx-auto px-6">
                <div className="max-w-3xl text-white">
                  <motion.h2
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-5xl md:text-6xl font-bold mb-6 leading-tight"
                  >
                    {slides[currentSlide].title}
                  </motion.h2>

                  <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-xl mb-8 text-gray-200 leading-relaxed max-w-2xl"
                  >
                    {slides[currentSlide].subtitle}
                  </motion.p>

                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Link
                      to="/services/aws-services"
                      className={`inline-block px-8 py-3 rounded-full font-bold text-white transition-all transform hover:scale-105 shadow-lg ${slides[currentSlide].color}`}
                    >
                      {slides[currentSlide].button}
                    </Link>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Progress Indicators */}
        <div className="absolute bottom-10 left-0 w-full z-20">
          <div className="container mx-auto px-6 flex items-center gap-4">
            <span className="text-sm font-medium text-white/80 hidden md:block">Highlights</span>
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className="h-[2px] flex-1 max-w-[100px] bg-white/30 rounded-full overflow-hidden transition-all hover:h-[4px]"
              >
                <div
                  className={`h-full bg-white transition-all duration-[5000ms] ease-linear ${index === currentSlide ? 'w-full' : 'w-0'}`}
                ></div>
              </button>
            ))}
          </div>
        </div>
      </section>




      {/* Transformation Agenda Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-6xl font-bold leading-tight text-gray-900">
              Shaping the Next Era of <br />
              Intelligent Transformation
            </h2>
          </motion.div>

          <div className="space-y-0">
            {/* Block 1 - Dark */}
            <div className="sticky top-24 z-10 grid md:grid-cols-2 bg-[#0F0524] text-white min-h-[500px]">
              <div className="p-12 md:p-16 flex flex-col justify-center">
                <h3 className="text-3xl md:text-4xl font-bold mb-6">Unlock the AI-Enabled Enterprise</h3>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  By integrating applied, agentic, and generative AI, organizations can optimize processes, enhance decision-making, and provide personalized experiences, all while boosting efficiency, creativity, and business resilience.
                </p>
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  Dhyon Technologies is helping organizations implement proven solutions and the required change management to navigate this era.
                </p>

                {/* <div className="flex flex-col gap-3">
                  <Link to="/services/aws-services" className="text-lg font-bold hover:text-purple-400 transition-colors flex items-center gap-2">
                    Explore More <FaArrowRight className="text-sm" />
                  </Link>
                  <Link to="/ai-engineering" className="text-lg font-bold text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
                    AI-Native Engineering <FaArrowRight className="text-sm" />
                  </Link>
                </div> */}
              </div>
              <div className="relative min-h-[400px] h-full">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1469&q=80)' }}
                ></div>
              </div>
            </div>

            {/* Block 2 - Light */}
            <div className="sticky top-24 z-20 grid md:grid-cols-2 bg-gray-50 min-h-[500px] shadow-[0_-10px_40px_rgba(0,0,0,0.1)]">
              <div className="relative min-h-[400px] order-2 md:order-1 h-full bg-[#060813]">
                <div
                  className="absolute inset-0 bg-contain bg-center bg-no-repeat"
                  style={{ backgroundImage: `url(${softDeveImg})` }}
                ></div>
              </div>
              <div className="p-12 md:p-16 flex flex-col justify-center order-1 md:order-2">
                <h3 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Software Development & Consultancy</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Dhyon Technologies is a forward-thinking digital solutions and technology services company dedicated to helping businesses thrive in the age of digital transformation. We specialize in AI-driven innovation, software engineering, cloud and data modernization, and scalable digital products and platforms that empower organizations to innovate faster, optimize operations, and deliver exceptional customer experiences.                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                </p>

                {/* <Link to="/services/automation" className="text-lg font-bold text-gray-900 hover:text-purple-700 transition-colors flex items-center gap-2">
                  Learn More <FaArrowRight className="text-sm" />
                </Link> */}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Why Dhyon Section - Dark Theme */}
      <section className="py-24 bg-[#0F0524] text-white">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-5xl md:text-7xl font-bold mb-16"
          >
            Why Dhyon
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1 */}
            <div className="bg-[#1A1033] p-8 h-[450px] flex flex-col justify-between rounded-sm hover:bg-[#251845] transition-colors duration-300 group">
              <div>
                <h3 className="text-2xl font-bold mb-6 leading-snug">End-to-End Digital Transformation Partner</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Dhyon Technologies is a trusted software development and digital transformation partner, delivering end-to-end solutions across strategy, design, development, deployment, and continuous optimization.
                </p>
              </div>
              <div>
                <FaRocket className="text-6xl font-light text-white/90 group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform duration-500" />
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-[#1A1033] p-8 h-[450px] flex flex-col justify-between rounded-sm hover:bg-[#251845] transition-colors duration-300 group">
              <div>
                <h3 className="text-2xl font-bold mb-6 leading-snug">Local Expertise, with Global Delivery</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  With strong local presence and global delivery capabilities, we combine domain expertise, industry best practices, and scalable offshore–onsite models to deliver reliable, cost-effective solutions worldwide.
                </p>
              </div>
              <div>
                <FaGlobeAmericas className="text-6xl font-light text-white/90 group-hover:scale-110 transition-transform duration-500" />
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-[#1A1033] p-8 h-[450px] flex flex-col justify-between rounded-sm hover:bg-[#251845] transition-colors duration-300 group">
              <div>
                <h3 className="text-2xl font-bold mb-6 leading-snug">AI-Driven Solutions & Technology Accelerators</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  We build AI-powered, cloud-native, and automation-ready solutions using proven accelerators to help businesses scale faster, improve efficiency, and gain data-driven insights.
                </p>
              </div>
              <div className="border-2 border-white/80 w-20 h-20 flex items-center justify-center rounded text-3xl font-bold group-hover:border-white transition-colors">
                AI
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-[#1A1033] p-8 h-[450px] flex flex-col justify-between rounded-sm hover:bg-[#251845] transition-colors duration-300 group">
              <div>
                <h3 className="text-2xl font-bold mb-6 leading-snug">Innovation-Focused Digital Experts</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Our team of innovation-driven technologists is passionate about emerging technologies, continuous learning, and open-source collaboration, helping clients stay competitive in a rapidly evolving digital landscape.
                </p>
              </div>
              <div>
                <FaChartLine className="text-6xl font-light text-white/90 group-hover:scale-110 transition-transform duration-500" />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12 border-t border-gray-800 pt-16">
            <div className="md:w-1/2">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Let's discuss how we can support your journey.</h2>
              <p className="text-gray-400 text-lg">Ready to transform your business? Get in touch with our experts today.</p>
            </div>
            <div className="md:w-1/3 flex flex-col gap-4 w-full">
              <Link to="/contact" className="w-full text-center py-4 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-colors text-lg">
                Contact Us
              </Link>
              <Link to="/services/aws-services" className="w-full text-center py-4 bg-transparent border border-gray-700 hover:border-gray-500 text-white font-bold rounded-lg transition-colors">
                View Cloud Services
              </Link>
              <Link to="/services/ai" className="w-full text-center py-4 bg-transparent border border-gray-700 hover:border-gray-500 text-white font-bold rounded-lg transition-colors">
                View AI Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DevelopmentPage;
